import { HUB_EVENTS, hubEmitter } from '@/core/realtime';
import { Conversation } from '@/features/conversation';
import { updateConversationInCache } from '@/features/messages';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';

export interface ConversationAddedEvent {
  id: string;
  name: string;
  description: string | null;
  type: 'Group' | 'Direct';
  avatarUrl: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  participants: any[];
  isActive: boolean;
  lastMessageId: string;
  lastMessageContent: string;
  lastMessageTimestamp: string | null;
  lastMessageSenderId: string;
  participantCount: number;
  unreadCount: number;
  settings: {
    addedAt: string;
    addedByUserId: string;
  };
  metadata: string;
}

/**
 * Transform ConversationAddedEvent to Conversation model
 */
const transformToConversation = (event: ConversationAddedEvent): Conversation => {
  return new Conversation({
    conversationId: event.id,
    conversationName: event.name,
    conversationType: event.type === 'Group' ? 'GROUP' : 'DIRECT',
    conversationAvatarRef: event.avatarUrl || undefined,
    createdAt: event.createdAt,
    updatedAt: event.updatedAt ? dayjs(event.updatedAt) : undefined,
    lastMessageId: event.lastMessageId || null,
    lastMessageContent: event.lastMessageContent || null,
    lastMessageTimestamp: event.lastMessageTimestamp || null,
    lastMessageSenderId: event.lastMessageSenderId || null,
    unreadCount: event.unreadCount || 0,
    muted: false,
    metadata: event.metadata || null,
    sortTimestamp: event.updatedAt ? dayjs(event.updatedAt) : dayjs()
  });
};

export const useConversationAddedEvent = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleConversationAdded = (addedEvent: ConversationAddedEvent) => {
      if (!addedEvent?.id) {
        return;
      }

      const conversationId = addedEvent.id;
      const newConversation = transformToConversation(addedEvent);
      updateConversationInCache(queryClient, conversationId, newConversation, 'add');
    };

    // Register hub event handler
    hubEmitter.on(HUB_EVENTS.MESSAGE.CONVERSATION_ADDED, handleConversationAdded);

    // Cleanup
    return () => {
      hubEmitter.off(HUB_EVENTS.MESSAGE.CONVERSATION_ADDED, handleConversationAdded);
    };
  }, [queryClient]);
};
